import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { ApplicationUtilsService, InputGewijzigdService } from '@util-services';
import { EcertBaseBlockComponent } from '@building-blocks/ecertnl-base-block/ecertnl-base-block.component';
import { Optie, OptieStatus } from '@models';
import { isEmptyValue, isEmptyString } from '@helpers';

export interface EcertOption {
  value: string;
  label: string;
}

@Component({
  selector: 'ecert-select',
  templateUrl: './ecertnl-select.component.html',
  styleUrls: ['./ecertnl-select.component.css'],
  standalone: false,
})
export class EcertSelectComponent extends EcertBaseBlockComponent {
  ref_objectPostCheck = this.reevaluateSelected;

  @Input() id: any;

  @Input() canSelectEmptyValue = true;

  private _opties: any[];
  @Input()
  set opties(opties: any[] | Optie[]) {
    this._opties = opties;
    this.reevaluateSelected();
  }
  get opties(): any[] {
    return this._opties?.filter((optie) => (isEmptyValue(optie.status) ? true : optie.status !== OptieStatus.HIDDEN));
  }

  private _optionValueProperty: any;
  @Input()
  set optionValueProperty(ovp: any) {
    this._optionValueProperty = ovp;
    this.reevaluateSelected();
  }
  get optionValueProperty(): any {
    return this._optionValueProperty;
  }

  private originalValue: any;
  private originalValueInitialised = false;
  private reevaluateSelected(): void {
    // Als verplicht en er is maar 1 optie, dan wordt de select ingevuld met deze optie.
    if (!!this.ref_object && !!this.property) {
      if (!this.originalValueInitialised) {
        this.originalValue = this.ref_object[this.property];
        this.originalValueInitialised = true;
      }

      if (!this.vulAutomatisch) {
        this.ref_object[this.property] = this.originalValue;
      } else if (
        this.originalValueInitialised &&
        this.originalValue === null &&
        !!this._opties &&
        this._opties.length === 1 &&
        !!this._optionValueProperty
      ) {
        this.ref_object[this.property] = this._opties[0][this._optionValueProperty];
        this.valueChange.emit({ target: { id: this.id }, newValue: this.ref_object[this.property] });
        this.inputGewijzigdService.inputGewijzigd();
      }
    }
  }

  @Input() optionLabelProperty: any;
  @Input() noEmptyValue = false;
  @Input() hoofdletter = false;

  @Output() selectFocus = new EventEmitter();

  @ViewChild('select')
  selectElement: ElementRef;

  handleChange(event) {
    this.ref_object[this.property] = event.target.value;
    if (this.isAdresRelatieBron()) {
      this.selectElement.nativeElement.blur();
    }
    event.newValue = this.ref_object[this.property];
    this.valueChange.emit(event);

    this.inputGewijzigdService.inputGewijzigd();
  }

  getActieveOptieLabel(): string {
    const opties = this._opties;
    if (isEmptyValue(opties)) {
      return '';
    }
    const optie = opties.find((optie) => optie[this.optionValueProperty] === this.ref_object[this.property]);
    if (isEmptyValue(optie)) {
      return '';
    }
    return optie[this.optionLabelProperty];
  }

  isAdresRelatieBron() {
    return this.id == 'adresRelatieBron';
  }

  focus(event) {
    if (this.isAdresRelatieBron()) {
      // selectedIndex aanpassen zodat het change-event ook wordt gestart als dezelfde optie wordt gekozen
      // (dit is nodig om te zorgen dat bij het opnieuw selecteren van een adres bron de eventueel al geselecteerde
      // adres wordt leeg gemaakt)
      this.selectElement.nativeElement.selectedIndex = -1;
      this.selectFocus.emit(event);
    }
  }

  focusOut(event) {
    if (this.isAdresRelatieBron()) {
      // onFocus wordt getriggerd om te zorgen dat de parent component eventueel iets kan doen wanneer de select van het
      // adres relatie bron dropdown verliest de focus
      this.selectFocus.emit(event);
    }
  }

  constructor(appUtils: ApplicationUtilsService, private inputGewijzigdService: InputGewijzigdService) {
    super(appUtils);
  }

  hasMargin() {
    return true;
  }

  hasErrors(): boolean {
    return false;
  }

  protected isDisabled(optie): boolean {
    return isEmptyValue(optie.status) ? false : optie.status === OptieStatus.DISABLED;
  }

  protected isHidden(optie): boolean {
    return isEmptyValue(optie.status) ? false : optie.status === OptieStatus.HIDDEN;
  }

  protected placeholderAlsOptieNodig(): boolean {
    if (this.noEmptyValue) {
      return false;
    }
    if (!this.verplicht) {
      return true;
    }
    if (isEmptyValue(this.ref_object)) {
      return true;
    }
    return isEmptyString(this.ref_object[this.property]);
  }

  protected placeholderOptieWaarde(): string {
    if (isEmptyValue(this.ref_object) || isEmptyValue(this.property)) {
      return '';
    }
    if (isEmptyValue(this.ref_object[this.property])) {
      return this.ref_object[this.property];
      // !! als de placeholder-optie gekozen wordt, dan de waarde van de property niet wijzigen
      //      i.v.m. niet activeren opslaan-button
      //      dus undefined, null, '' blijft exact hetzelfde
    }
    return '';
  }
}
